import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import FolderIcon from '@mui/icons-material/Folder';

import PropTypes from 'prop-types';
import predefinedCategorias from '../../../../constants/predefinedCategorias';

function subtitleFor(cat) {
  const parts = [];
  if (cat.code || cat.codigo) parts.push(`Código: ${cat.code ?? cat.codigo}`);
  if (
    (cat.edadMin !== undefined && cat.edadMin !== null) ||
    (cat.edadMax !== undefined && cat.edadMax !== null)
  ) {
    const min = cat.edadMin ?? '—';
    const max = cat.edadMax ?? '—';
    parts.push(`Edades: ${min}–${max}`);
  }
  return parts.join(' • ');
}

export default function PredefinedCategoriasModal({
  open,
  handleClose,
  handleAddSelectedCategorias,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(new Set());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return predefinedCategorias;
    return predefinedCategorias.filter((c) => {
      const inNombre = c?.nombre?.toLowerCase().includes(q);
      const inCode = (c?.code || c?.codigo || '').toLowerCase().includes(q);
      return inNombre || inCode;
    });
  }, [search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const onConfirm = () => {
    const categoriasToAdd = predefinedCategorias
      .filter((c) => selected.has(c.id))
      .map((c) => ({
        ...c,
        codigo: c.codigo ?? c.code ?? '',
        nombre: c.nombre ?? '',
      }));
    handleAddSelectedCategorias(categoriasToAdd);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      {/* Header sticky con X */}
      <Box
        sx={(t) => ({
          position: 'sticky',
          top: 0,
          zIndex: 3,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${t.palette.divider}`,
          bgcolor: t.palette.background.paper,
        })}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Añadir Categorías
        </Typography>
        <IconButton onClick={handleClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Contenido */}
      <Box sx={{ p: 2, pb: 10 }}>
        {/* Buscador pill */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1,
            mb: 2,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          }}
        >
          <SearchIcon sx={{ opacity: 0.6 }} />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o código…"
            inputProps={{ 'aria-label': 'buscar categorías' }}
            sx={{ flex: 1, fontSize: '1rem' }}
          />
        </Box>

        {/* Lista */}
        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
          Categorías predefinidas
        </Typography>

        <List dense disablePadding sx={{ mt: 1 }}>
          {filtered.map((c, idx) => {
            const isSel = selected.has(c.id);
            return (
              <React.Fragment key={c.id}>
                <ListItem
                  sx={(t) => ({
                    borderRadius: 3,
                    px: 1.25,
                    py: 1.25,
                    mb: 1.25,
                    bgcolor: alpha(t.palette.common.black, 0.02),
                    border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
                  })}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                      <FolderIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                        {c.nombre}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ color: 'text.secondary', mt: 0.25 }}>
                        {subtitleFor(c)}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => toggle(c.id)}
                      aria-label={isSel ? 'deseleccionar' : 'seleccionar'}
                      sx={(t) => ({
                        width: 40,
                        height: 40,
                        borderRadius: '999px',
                        bgcolor: isSel
                          ? alpha(t.palette.primary.main, 0.12)
                          : alpha(t.palette.text.primary, 0.06),
                        border: `1px solid ${alpha(t.palette.text.primary, 0.15)}`,
                      })}
                    >
                      {isSel ? <CheckIcon color="primary" /> : <AddIcon />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < filtered.length - 1 && <Divider sx={{ opacity: 0.15 }} />}
              </React.Fragment>
            );
          })}

          {filtered.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No hay categorías que coincidan con tu búsqueda.</Typography>
            </Box>
          )}
        </List>
      </Box>

      {/* Footer sticky — estilos iguales al modal de categorías personalizadas */}
<Box
  sx={(t) => ({
    position: 'sticky',
    bottom: 0,
    zIndex: 3,
    px: 2,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',   // 👈 juntos a la derecha
    gap: 2,                       // 👈 espacio entre botones
    borderTop: `1px solid ${t.palette.divider}`,
    bgcolor: t.palette.background.paper,
  })}
>
  <Button
    onClick={handleClose}
    variant="outlined"
    sx={(t) => ({
      borderRadius: 999,
      px: 2.5,
      py: 1,
      textTransform: 'none',
      fontWeight: 700,
      color: t.palette.secondary.main,
      border: `1px solid ${t.palette.secondary.main}`,
      '&:hover': { bgcolor: t.palette.action.hover },
    })}
  >
    Cancelar
  </Button>

  <Button
    onClick={onConfirm}
    disabled={selected.size === 0}
    variant="contained"
    sx={{
      borderRadius: 999,
      px: 2.5,
      py: 1,
      textTransform: 'none',
      fontWeight: 700,
    }}
  >
    {selected.size > 0
      ? `Añadir ${selected.size} categoría${selected.size > 1 ? 's' : ''}`
      : 'Añadir Categorías'}
  </Button>
</Box>
    </Dialog>
  );
}

PredefinedCategoriasModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAddSelectedCategorias: PropTypes.func.isRequired,
};
